import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface MasterDetailLayoutProps {
  /** Content rendered in the left (list) panel */
  listContent: React.ReactNode;
  /** Content rendered in the right (detail) panel; null shows empty state */
  detailContent: React.ReactNode | null;
  /** Message shown when no detail is selected */
  emptyDetailMessage?: string;
  /** Initial width of the list panel in pixels @default 420 */
  listWidth?: number;
  /** Minimum allowed list panel width in pixels @default 300 */
  minListWidth?: number;
  /** Maximum allowed list panel width in pixels @default 600 */
  maxListWidth?: number;
  /** Callback when list panel width changes (e.g. after drag) */
  onListWidthChange?: (width: number) => void;
  /** Additional CSS class for the root container */
  className?: string;
}

/**
 * Master-detail (inbox) layout with a resizable left list panel and a right detail panel.
 * Uses Meridian Dark design system tokens exclusively.
 *
 * @example
 * ```tsx
 * <MasterDetailLayout
 *   listContent={<RecordList />}
 *   detailContent={selectedRecord ? <RecordDetail /> : null}
 *   emptyDetailMessage="Select a record to view details"
 *   listWidth={360}
 *   onListWidthChange={(w) => saveToPrefs(w)}
 * />
 * ```
 */
export const MasterDetailLayout = React.forwardRef<
  HTMLDivElement,
  MasterDetailLayoutProps
>(
  (
    {
      listContent,
      detailContent,
      emptyDetailMessage,
      listWidth: controlledWidth,
      minListWidth = 300,
      maxListWidth = 600,
      onListWidthChange,
      className,
    },
    ref
  ) => {
    const { t } = useTranslation('common');
    const [internalWidth, setInternalWidth] = useState(controlledWidth ?? 420);
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);
    const rafId = useRef<number>(0);

    // Sync internal state when controlled prop changes
    const currentWidth = controlledWidth ?? internalWidth;

    const clamp = useCallback(
      (value: number) => Math.min(maxListWidth, Math.max(minListWidth, value)),
      [minListWidth, maxListWidth]
    );

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isDragging.current) return;

        // Cancel any pending rAF to avoid stacking frames
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
        }

        rafId.current = requestAnimationFrame(() => {
          const delta = e.clientX - startX.current;
          const newWidth = clamp(startWidth.current + delta);
          setInternalWidth(newWidth);
          onListWidthChange?.(newWidth);
        });
      },
      [clamp, onListWidthChange]
    );

    const handleMouseUp = useCallback(() => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = 0;
      }

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        startX.current = e.clientX;
        startWidth.current = currentWidth;

        // Prevent text selection and set resize cursor globally during drag
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      },
      [currentWidth, handleMouseMove, handleMouseUp]
    );

    // Keyboard support for the divider
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const step = e.shiftKey ? 50 : 10;
        let newWidth = currentWidth;

        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          newWidth = clamp(currentWidth - step);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          newWidth = clamp(currentWidth + step);
        } else {
          return;
        }

        setInternalWidth(newWidth);
        onListWidthChange?.(newWidth);
      },
      [currentWidth, clamp, onListWidthChange]
    );

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, [handleMouseMove, handleMouseUp]);

    // Observe container width to clamp list panel responsively
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) setContainerWidth(entry.contentRect.width);
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    // Cap list width at 40% of container so detail panel always has room
    const effectiveMaxWidth =
      containerWidth > 0
        ? Math.min(maxListWidth, Math.floor(containerWidth * 0.4))
        : maxListWidth;
    const effectiveWidth = Math.min(currentWidth, effectiveMaxWidth);
    const effectiveMinWidth = Math.min(minListWidth, effectiveMaxWidth);

    return (
      <div
        ref={(node) => {
          containerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={cn('flex h-full overflow-hidden', className)}
      >
        {/* List panel */}
        <div
          className="flex flex-col overflow-hidden border-r border-border bg-bg"
          style={{
            width: effectiveWidth,
            minWidth: effectiveMinWidth,
            maxWidth: effectiveMaxWidth,
          }}
        >
          {listContent}
        </div>

        {/* Resizable divider */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={currentWidth}
          aria-valuemin={minListWidth}
          aria-valuemax={maxListWidth}
          aria-label={t('layout.resizeListPanel')}
          tabIndex={0}
          className={cn(
            'relative z-10 w-1 shrink-0 cursor-col-resize',
            'bg-transparent transition-colors duration-150',
            'hover:bg-border focus-visible:bg-border',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
        />

        {/* Detail panel */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {detailContent !== null ? (
            detailContent
          ) : (
            <div className="flex flex-1 items-center justify-center text-fg-muted">
              <div className="text-center">
                <FileText className="mx-auto mb-3 h-12 w-12 opacity-30" />
                <p className="text-sm">
                  {emptyDetailMessage || t('layout.selectRecordFromList')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

MasterDetailLayout.displayName = 'MasterDetailLayout';
